import { reportingApi } from "@/api/reporting/reporting.api.ts";
import { filtersLibrary, periodsLibrary } from "@/components/filter/filter.types.ts";
import { DataBarsRow } from "@/components/data-bars/DataBars.tsx";
import { FilterChoosePeriod } from "@/components/filter/FilterChoosePeriod.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { KpiTile } from "@/components/reporting/KpiTile.tsx";
import { LineChart } from "@/components/reporting/LineChart.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import { DimensionBarsWidget } from "@/pages/reporting/components/DimensionBarsWidget.tsx";
import { ReportingWidget } from "@/pages/reporting/components/ReportingWidget.tsx";
import { useReportViewFilter } from "@/pages/reporting/components/use-report-view-filter.ts";
import {
  widgetPointEntityIds,
  widgetSeriesToLineData,
} from "@/pages/reporting/components/widget-chart.adapter.ts";
import { PeopleAnalyticsCacheStatus } from "@/pages/reporting/employee/people-analytics/components/PeopleAnalyticsCacheStatus.tsx";
import { EmployeeSalaryReportTableV2 } from "@/pages/reporting/employee/salary-v2/components/EmployeeSalaryReportTableV2.tsx";
import {
  useSalaryDashboard,
  useSalaryWidget,
} from "@/pages/reporting/employee/salary-v2/use-salary-widget.ts";
import { EmployeeSalaryReportType } from "@/pages/reporting/employee/salary/employee-salary-report.types.ts";
import { useCurrencies } from "@/queries/currencies.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  AnalyticReportTypes,
  ApiAnalyticsAggregationDate,
} from "@/types/analytics/analytics.types.ts";
import {
  ApiPeopleAnalyticsPayrollAllocation,
  ApiPeopleAnalyticsWarningCode,
  ApiPeopleAnalyticsWidgetBucketSort,
} from "@/types/reporting/people-analytics.types.ts";
import {
  ApiReportingDetailsFormat,
  ApiReportingDetailsInput,
  ApiReportingDetailsSortDirection,
  ApiReportingDetailsSortField,
  ApiWidgetCompensationOptions,
  ApiWidgetData,
  ApiWidgetDimension,
  ApiWidgetFilter,
  ApiWidgetFilterField,
  ApiWidgetMetric,
  ApiWidgetPeriodMode,
  ApiWidgetPreviewInput,
  ApiWidgetType,
} from "@/types/reporting/reporting.types.ts";
import {
  ApiAggregationDate,
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  Page,
  Select,
  SelectItem,
  Skeleton,
  Stack,
  TabItem,
  Tabs,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { CompensationRollupType } from "@/types/analytics/analytics.employee.types.ts";
import { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import {
  getAvailableCompensationRollups,
  getAvailableSalaryMetrics,
  resolveCompensationRollup,
  resolveSalaryMetric,
  SalaryReportMetric,
} from "@/pages/reporting/employee/salary/compensation-rollup.utils.ts";

interface SalaryV2Filter {
  aggregation_date_mode?: ApiAnalyticsAggregationDate;
  date_from?: Date;
  date_to?: Date;
  job_profile_ids?: number[];
  job_role_ids?: number[];
  department_ids?: number[];
  division_ids?: number[];
  location_ids?: number[];
  employment_type_ids?: number[];
  employment_contract_ids?: number[];
  employee_lifecycle_statuses?: ApiEmployeeLifecycleStatus[];
}

const previewLifecycleStatuses = Object.values(ApiEmployeeLifecycleStatus);

const DETAILS_SORT_FIELDS = new Set<string>(Object.values(ApiReportingDetailsSortField));

type SalaryMetric = SalaryReportMetric;
type SalaryType = CompensationRollupType;

interface EmployeeSalaryReportV2Props {
  type: EmployeeSalaryReportType;
}

export function EmployeeSalaryReportV2({ type }: EmployeeSalaryReportV2Props) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { formatPeriodDates, renderCurrency } = useFormatter();
  const { downloadFile, isExporting } = useDownloadHelper();
  const { canIView } = usePermissions();
  const { dashboard: salaryDashboard, isLoading: isDashboardLoading } = useSalaryDashboard();
  const canViewTotalCost = !!salaryDashboard?.widgets.some(
    (item) => item.widget.system_key === "salary.kpi.total_cost"
  );
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currencySearch, setCurrencySearch] = useState("");

  const reportTitle = t("reporting:salary_report_title");
  useTitle(reportTitle, t("common:reports"));

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.reporting.viewed, {
      [analyticProperties.title]: AnalyticReportTypes.employeesSalary,
    });
  });

  const library = filtersLibrary();
  const periodLibrary = periodsLibrary();
  const { data: currencies } = useCurrencies();

  const selectedCurrency = searchParams.get("currency");
  const requestedRollup = searchParams.get("rollup_type") ?? searchParams.get("salary_type");
  const salaryType: SalaryType = useMemo(
    () => resolveCompensationRollup(requestedRollup, canViewTotalCost),
    [requestedRollup, canViewTotalCost]
  );
  const metricParam = searchParams.get("metric") as SalaryMetric | null;
  const metric = resolveSalaryMetric(metricParam, canViewTotalCost);
  const allocation =
    Object.values(ApiPeopleAnalyticsPayrollAllocation).find(
      (value) => value === searchParams.get("allocation")
    ) ??
    salaryDashboard?.default_compensation?.allocation ??
    ApiPeopleAnalyticsPayrollAllocation.by_days;
  const breakdownAllocation =
    metric !== ApiWidgetMetric.average_salary && metric !== ApiWidgetMetric.median_salary
      ? allocation
      : undefined;

  const baseEmployeeFilters = [
    library.job_profile_ids,
    library.job_role_ids,
    library.department_ids,
    library.division_ids,
    library.location_ids,
    library.employment_type_ids,
    library.employment_contract_ids,
    library.employee_lifecycle_statuses,
  ];

  const {
    filter: { filter },
    filterButton,
    config,
    isFilterReady,
  } = useAppFilter<SalaryV2Filter>({
    filters: [
      library.aggregation_date_mode,
      { ...library.date_from, hideInFilter: true },
      { ...library.date_to, hideInFilter: true },
      { ...library.period, defaultValue: "rolling_year" },
      ...baseEmployeeFilters,
    ],
    source: AnalyticReportTypes.employeesSalary,
  });
  const viewFilterApplied = useReportViewFilter(baseEmployeeFilters);

  const widgetFilters = useMemo<ApiWidgetFilter[]>(() => {
    const result: ApiWidgetFilter[] = [];
    const push = (field: ApiWidgetFilterField, ids?: number[]) => {
      if (ids?.length) result.push({ field, ids });
    };
    push(ApiWidgetFilterField.job_profile_ids, filter.job_profile_ids);
    push(ApiWidgetFilterField.job_role_ids, filter.job_role_ids);
    push(ApiWidgetFilterField.department_ids, filter.department_ids);
    push(ApiWidgetFilterField.division_ids, filter.division_ids);
    push(ApiWidgetFilterField.location_ids, filter.location_ids);
    push(ApiWidgetFilterField.employment_type_ids, filter.employment_type_ids);
    push(ApiWidgetFilterField.employment_contract_ids, filter.employment_contract_ids);
    return result;
  }, [filter]);

  const period = useMemo(
    () => ({
      mode: ApiWidgetPeriodMode.absolute,
      date_from: filter.date_from,
      date_to: filter.date_to,
    }),
    [filter.date_from, filter.date_to]
  );
  const compensation = useMemo<ApiWidgetCompensationOptions>(
    () => ({ rollup_type: salaryType, currency: selectedCurrency ?? undefined }),
    [salaryType, selectedCurrency]
  );
  const ready = viewFilterApplied && !!filter.date_from && !!filter.date_to;
  const detailsReady = ready && isFilterReady && !isDashboardLoading;
  const isTable = type === EmployeeSalaryReportType.table;
  const previewEnabled = ready && !isTable;
  const dateGrain = filter.aggregation_date_mode ?? ApiAnalyticsAggregationDate.month;

  const buildPreviewInput = useCallback(
    (
      metric: SalaryMetric,
      widgetType: ApiWidgetType,
      selectedDimension?: ApiWidgetDimension
    ): ApiWidgetPreviewInput => ({
      widget_type: widgetType,
      period,
      query: {
        metrics: [metric],
        dimensions: selectedDimension ? [selectedDimension] : undefined,
        filters: widgetFilters,
        date_grain: widgetType === ApiWidgetType.line_chart ? dateGrain : undefined,
        compensation,
      },
    }),
    [compensation, dateGrain, period, widgetFilters]
  );

  const totalPayrollKpi = useSalaryWidget(
    buildPreviewInput(ApiWidgetMetric.total_payroll, ApiWidgetType.kpi),
    previewEnabled
  );
  const averageSalaryKpi = useSalaryWidget(
    buildPreviewInput(ApiWidgetMetric.average_salary, ApiWidgetType.kpi),
    previewEnabled
  );
  const medianSalaryKpi = useSalaryWidget(
    buildPreviewInput(ApiWidgetMetric.median_salary, ApiWidgetType.kpi),
    previewEnabled
  );
  const totalBonusKpi = useSalaryWidget(
    buildPreviewInput(ApiWidgetMetric.total_bonus, ApiWidgetType.kpi),
    previewEnabled
  );
  const totalCostKpi = useSalaryWidget(
    buildPreviewInput(ApiWidgetMetric.total_cost, ApiWidgetType.kpi),
    previewEnabled && canViewTotalCost
  );
  const employerContributionsKpi = useSalaryWidget(
    buildPreviewInput(ApiWidgetMetric.employer_contributions, ApiWidgetType.kpi),
    previewEnabled && canViewTotalCost
  );
  const metricChart = useSalaryWidget(
    buildPreviewInput(metric, ApiWidgetType.line_chart),
    previewEnabled
  );
  const departmentsChart = useSalaryWidget(
    buildPreviewInput(metric, ApiWidgetType.bar_chart, ApiWidgetDimension.department),
    previewEnabled,
    breakdownAllocation
  );
  const divisionsChart = useSalaryWidget(
    buildPreviewInput(metric, ApiWidgetType.bar_chart, ApiWidgetDimension.division),
    previewEnabled,
    breakdownAllocation
  );
  const locationsChart = useSalaryWidget(
    buildPreviewInput(metric, ApiWidgetType.bar_chart, ApiWidgetDimension.location),
    previewEnabled,
    breakdownAllocation
  );
  const jobProfilesChart = useSalaryWidget(
    buildPreviewInput(metric, ApiWidgetType.bar_chart, ApiWidgetDimension.job_profile),
    previewEnabled,
    breakdownAllocation
  );
  const jobRolesChart = useSalaryWidget(
    buildPreviewInput(metric, ApiWidgetType.bar_chart, ApiWidgetDimension.job_role),
    previewEnabled,
    breakdownAllocation
  );
  const lastUpdatedChart = useSalaryWidget(
    buildPreviewInput(metric, ApiWidgetType.bar_chart, ApiWidgetDimension.salary_last_updated),
    previewEnabled
  );
  const metricTitles = useMemo<Record<SalaryMetric, string>>(
    () => ({
      [ApiWidgetMetric.total_payroll]: t("reporting:metric_total_payroll"),
      [ApiWidgetMetric.average_salary]: t("reporting:metric_average_salary"),
      [ApiWidgetMetric.median_salary]: t("reporting:metric_median_salary"),
      [ApiWidgetMetric.total_bonus]: t("reporting:metric_total_bonus"),
      [ApiWidgetMetric.total_cost]: t("reporting:metric_total_cost"),
      [ApiWidgetMetric.employer_contributions]: t("reporting:metric_employer_contributions"),
    }),
    [t]
  );

  const metricOptions = useMemo<SelectItem[]>(
    () =>
      getAvailableSalaryMetrics(canViewTotalCost).map((value) => ({
        id: value,
        title: metricTitles[value],
      })),
    [canViewTotalCost, metricTitles]
  );
  const currenciesMapped = useMemo<SelectItem[]>(
    () =>
      currencies?.map((currency) => ({
        id: currency.code,
        title: `${currency.code} (${currency.symbol_native})`,
      })) ?? [],
    [currencies]
  );
  const shownCurrency = selectedCurrency ?? totalPayrollKpi.data?.meta.currency ?? null;
  const selectedCurrencyTitle =
    currenciesMapped.find((currency) => currency.id === shownCurrency)?.title ??
    shownCurrency ??
    t("common:currency");

  const formatMoney = useCallback(
    (value: number | string | null | undefined, currency: string | null) =>
      renderCurrency(String(Number(value ?? 0)), currency),
    [renderCurrency]
  );
  const metricLineData = useMemo(
    () => widgetSeriesToLineData(metricChart.data, formatPeriodDates),
    [formatPeriodDates, metricChart.data]
  );

  const openEmployees = (employeeIds: number[], title: string) =>
    openModal(PreviewEmployeesModal, {
      filter: {
        page_number: 1,
        employee_ids: employeeIds,
        employee_lifecycle_statuses: previewLifecycleStatuses,
      },
      addColumns: [],
      modalTitle: title,
      source: `${AnalyticReportTypes.employeesSalary} V2`,
    });

  const handleLinePointClick = (seriesIndex: number, pointIndex: number) => {
    const employeeIds = widgetPointEntityIds(metricChart.data, pointIndex, seriesIndex);
    if (employeeIds.length) openEmployees(employeeIds, metricLineData.labels[pointIndex]);
  };

  const hasFxWarning = (data: ApiWidgetData | undefined) =>
    !!data?.meta.warnings.includes(ApiPeopleAnalyticsWarningCode.missing_fx_rate);
  const fxWarningText = (
    <Text size="sm">{t("reporting:people_analytics_warning_missing_fx_rate")}</Text>
  );

  const buildSalaryDimensionRows = useCallback(
    (data: ApiWidgetData | undefined): DataBarsRow[] => {
      const points = data?.series[0]?.points ?? [];
      const currency = data?.meta.currency ?? null;
      const maxValue = Math.max(
        ...points.flatMap((point) => [
          Math.abs(Number(point.value ?? 0)),
          Math.abs(Number(point.comparison_value ?? 0)),
        ]),
        1
      );

      return points.map((point) => {
        const value = point.value === null ? null : Number(point.value);
        const hasComparison = point.comparison_value !== null;
        const comparisonValue = hasComparison ? Number(point.comparison_value) : null;
        const delta =
          value === null || comparisonValue === null ? undefined : value - comparisonValue;
        const openEmployees = (employeeIds: number[]) => {
          if (!employeeIds.length) return;
          openModal(PreviewEmployeesModal, {
            filter: {
              page_number: 1,
              employee_ids: employeeIds,
              employee_lifecycle_statuses: previewLifecycleStatuses,
            },
            addColumns: [],
            modalTitle: point.label,
            source: `${AnalyticReportTypes.employeesSalary} V2`,
          });
        };

        const formattedValue = value === null ? "-" : formatMoney(value, currency);

        return {
          title: point.label,
          value: formattedValue,
          sortValue: value ?? -Infinity,
          valueDelta: delta,
          formatValueDelta: (deltaValue) =>
            `${deltaValue > 0 ? "+" : ""}${formatMoney(deltaValue, currency)}`,
          percent: value === null ? 0 : (value / maxValue) * 100,
          percentDelta: delta === undefined ? undefined : (delta / maxValue) * 100,
          emoji: null,
          tooltip: (
            <Stack gap="xs">
              <Text size="sm">
                {t("reporting:tooltip_current_period")}: {formattedValue}
              </Text>
              {comparisonValue !== null && (
                <Text size="sm">
                  {t("reporting:tooltip_previous_period")}: {formatMoney(comparisonValue, currency)}
                </Text>
              )}
            </Stack>
          ),
          onClick: point.entity_ids.length ? () => openEmployees(point.entity_ids) : undefined,
        };
      });
    },
    [formatMoney, openModal, t]
  );

  const setSalaryType = (value: SalaryType) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("rollup_type", value);
      return next;
    });
  };
  const setAllocation = (value: ApiPeopleAnalyticsPayrollAllocation) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("allocation", value);
      return next;
    });
  };
  const setCurrency = (value: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("currency", value);
      return next;
    });
  };
  const setMetric = (value: SalaryMetric) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("metric", value);
      return next;
    });
  };

  const handleTabChanged = useCallback(
    (tabIndex: number) => {
      const basePath = "/reporting/employee/salary-report-analytics";
      const path = tabIndex === 0 ? `${basePath}/graphs` : `${basePath}/table`;
      navigate(`${path}?${searchParams.toString()}`);
    },
    [navigate, searchParams]
  );

  const detailsInput = useMemo<ApiReportingDetailsInput>(
    () => ({
      period,
      compensation,
      filters: widgetFilters,
      employee_lifecycle_statuses: filter.employee_lifecycle_statuses,
      format: ApiReportingDetailsFormat.page,
    }),
    [compensation, filter.employee_lifecycle_statuses, period, widgetFilters]
  );

  const handleExport = useCallback(() => {
    const sortFieldParam = searchParams.get("sort_field");
    const sortField =
      sortFieldParam && DETAILS_SORT_FIELDS.has(sortFieldParam)
        ? (sortFieldParam as ApiReportingDetailsSortField)
        : ApiReportingDetailsSortField.employee_name;
    const sortDirection =
      searchParams.get("sort_direction") === ApiReportingDetailsSortDirection.desc
        ? ApiReportingDetailsSortDirection.desc
        : ApiReportingDetailsSortDirection.asc;

    downloadFile({
      apiCall: () =>
        reportingApi.detailsExport({
          ...detailsInput,
          page_number: 1,
          text: searchParams.get("search") || undefined,
          sorting: [{ field: sortField, direction: sortDirection }],
          format: ApiReportingDetailsFormat.xlsx,
        }),
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.dataExported, {
          [analyticProperties.title]: AnalyticReportTypes.employeesSalary,
        });
      },
    });
  }, [detailsInput, downloadFile, searchParams]);

  const kpis = [
    [ApiWidgetMetric.total_payroll, totalPayrollKpi],
    [ApiWidgetMetric.average_salary, averageSalaryKpi],
    [ApiWidgetMetric.median_salary, medianSalaryKpi],
    [ApiWidgetMetric.total_bonus, totalBonusKpi],
    ...(canViewTotalCost
      ? ([
          [ApiWidgetMetric.total_cost, totalCostKpi],
          [ApiWidgetMetric.employer_contributions, employerContributionsKpi],
        ] as const)
      : []),
  ] as const;

  const dimensionCharts = [
    {
      dimension: ApiWidgetDimension.department,
      title: t("reporting:salary_by_departments"),
      query: departmentsChart,
    },
    {
      dimension: ApiWidgetDimension.division,
      title: t("reporting:salary_by_divisions"),
      query: divisionsChart,
    },
    {
      dimension: ApiWidgetDimension.location,
      title: t("reporting:salary_by_locations"),
      query: locationsChart,
    },
    {
      dimension: ApiWidgetDimension.job_profile,
      title: t("reporting:salary_by_job_profiles"),
      query: jobProfilesChart,
    },
    {
      dimension: ApiWidgetDimension.job_role,
      title: t("reporting:salary_by_job_roles"),
      query: jobRolesChart,
    },
    {
      dimension: ApiWidgetDimension.salary_last_updated,
      title: t("reporting:last_salary_updated"),
      query: lastUpdatedChart,
    },
  ];

  return (
    <Page
      className={isTable ? "h-full" : "bg-light/80 h-full overflow-y-auto"}
      variant={isTable ? "default" : "opaque"}
    >
      <Page.Header title={reportTitle}>
        <Stack direction="horizontal" gap="sm" className="overflow-hidden flex-wrap">
          {canIView(ApiPermission.reporting_people_analytics) && <PeopleAnalyticsCacheStatus />}
          {!isTable && (
            <Select
              variant="button"
              options={metricOptions}
              value={metric}
              onChange={(value) => setMetric(value as SalaryMetric)}
              emptyTitle={t("common:select")}
            />
          )}
          <DropdownMenu
            trigger={
              <Button
                variant="secondary"
                rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
              >
                {salaryType === CompensationRollupType.total_cost
                  ? t("reporting:metric_total_cost")
                  : t(`reporting:${salaryType}`)}
              </Button>
            }
          >
            {getAvailableCompensationRollups(canViewTotalCost).map((value) => (
              <DropdownMenuItem
                key={value}
                title={
                  value === CompensationRollupType.total_cost
                    ? t("reporting:metric_total_cost")
                    : t(`reporting:${value}`)
                }
                isSelected={salaryType === value}
                onClick={() => setSalaryType(value)}
              />
            ))}
          </DropdownMenu>
          {!isTable && (
            <DropdownMenu
              trigger={
                <Button
                  variant="secondary"
                  rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
                >
                  {t(`reporting:salary_allocation_${allocation}`)}
                </Button>
              }
            >
              {Object.values(ApiPeopleAnalyticsPayrollAllocation).map((value) => (
                <DropdownMenuItem
                  key={value}
                  title={t(`reporting:salary_allocation_${value}`)}
                  isSelected={allocation === value}
                  onClick={() => setAllocation(value)}
                />
              ))}
            </DropdownMenu>
          )}
          <FilterChoosePeriod
            availableAggregationDateModes={[
              ApiAggregationDate.year,
              ApiAggregationDate.quarter,
              ApiAggregationDate.month,
            ]}
            staticPeriods={[
              periodLibrary.rolling_year,
              periodLibrary.year_to_date,
              periodLibrary.last_year_0,
              periodLibrary.last_year_2,
              periodLibrary.last_year_3,
            ]}
            filtersConfig={config}
          />
          <DropdownMenu
            trigger={
              <Button
                variant="secondary"
                rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
              >
                {selectedCurrencyTitle}
              </Button>
            }
          >
            <Box onClick={(event) => event.stopPropagation()}>
              <Input
                label={t("common:search")}
                placeholder={t("common:search_start_typing")}
                value={currencySearch}
                onChange={(event) => setCurrencySearch(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
              />
            </Box>
            {currenciesMapped
              .filter((currency) =>
                currency.title.toLowerCase().includes(currencySearch.toLowerCase())
              )
              .map((currency) => (
                <DropdownMenuItem
                  key={currency.id}
                  title={currency.title}
                  isSelected={shownCurrency === currency.id}
                  onClick={() => setCurrency(currency.id as string)}
                />
              ))}
          </DropdownMenu>
          {filterButton()}
          {isTable && (
            <Button
              onClick={handleExport}
              isLoading={isExporting}
              disabled={!ready || isDashboardLoading}
            >
              {t("common:export")}
            </Button>
          )}
        </Stack>
      </Page.Header>

      <Page.Content stretch={isTable}>
        <Tabs fill={isTable} activeTab={isTable ? 1 : 0} onActiveTabChange={handleTabChanged}>
          <TabItem title={t("common:overview")}>
            {!isTable && (
              <Stack gap="md" className={"pt-10"}>
                <Box className="grid md:grid-cols-4 gap-md py-lg">
                  {kpis.map(([kpiMetric, query]) => {
                    const isEmpty = query.data?.value === null || query.isError;
                    return (
                      <KpiTile
                        key={kpiMetric}
                        title={metricTitles[kpiMetric]}
                        titleIcon={
                          hasFxWarning(query.data) && (
                            <Tooltip>
                              <TooltipTrigger>
                                <FontAwesomeIcon icon={"fa-light fa-info-circle"} />
                              </TooltipTrigger>
                              <TooltipContent className={"max-w-[320px]"}>
                                {fxWarningText}
                              </TooltipContent>
                            </Tooltip>
                          )
                        }
                        value={isEmpty ? "-" : query.data?.value}
                        format={
                          isEmpty
                            ? undefined
                            : (value) => formatMoney(value, query.data?.meta.currency ?? null)
                        }
                      />
                    );
                  })}
                </Box>

                <ReportingWidget
                  title={metricTitles[metric]}
                  tooltip={hasFxWarning(metricChart.data) && fxWarningText}
                  content={
                    metricChart.data ? (
                      <LineChart
                        labels={metricLineData.labels}
                        series={[{ data: metricLineData.data }]}
                        formatTooltipValue={(value) =>
                          formatMoney(value, metricChart.data?.meta.currency ?? null)
                        }
                        formatAxisValue={(value) =>
                          formatMoney(value, metricChart.data?.meta.currency ?? null)
                        }
                        beginAtZero
                        onPointClick={handleLinePointClick}
                      />
                    ) : (
                      <Skeleton className="w-full h-[236px]" />
                    )
                  }
                />

                <Box className="grid gap-md lg:grid-cols-2">
                  {dimensionCharts.map(({ dimension, title, query }) => {
                    const rows = buildSalaryDimensionRows(query.data);

                    return (
                      <DimensionBarsWidget
                        key={dimension}
                        title={title}
                        caption={metricTitles[metric]}
                        tooltip={hasFxWarning(query.data) && fxWarningText}
                        rows={rows}
                        sortable={query.bucketSort !== ApiPeopleAnalyticsWidgetBucketSort.natural}
                      />
                    );
                  })}
                </Box>
              </Stack>
            )}
          </TabItem>
          <TabItem
            title={t("core:employees")}
            className={isTable ? "flex min-h-0 min-w-0 flex-1 flex-col" : undefined}
          >
            {isTable && (
              <EmployeeSalaryReportTableV2
                input={detailsInput}
                enabled={detailsReady}
                rollupType={salaryType}
              />
            )}
          </TabItem>
        </Tabs>
      </Page.Content>
    </Page>
  );
}
